import { useEffect, useMemo, useRef, useState } from "react";
import ModalSheet from "./ModalSheet";
import BarcodeScanner from "./BarcodeScanner";
import { Z, FONT, COLOR } from "../lib/tokens";
import { searchCanonicals, findIngredient, inferCanonicalFromName } from "../data/ingredients";
import { resolveNutrition, scaleFactor, effectiveCountWeightG } from "../lib/nutrition";
import { useConsumptionLogs, inferMealSlot } from "../lib/useConsumptionLogs";
import { usePopularPackages } from "../lib/usePopularPackages";
import { useBrandNutrition } from "../lib/useBrandNutrition";
import { useIngredientInfo } from "../lib/useIngredientInfo";
import { useToast } from "../lib/toast";
import { lookupBarcode } from "../lib/lookupBarcode";

// EatQuickSheet — the "I just ate something" path for users who don't
// manage pantry inventory. Writes a consumption_logs row with
// pantry_row_id: null. Optional add-to-shopping-list when the user
// flags they finished the package.
//
// Three entry vectors on the same sheet:
//   1. Type a name → live canonical typeahead → pick.
//   2. Tap SCAN → BarcodeScanner → OFF lookup → canonical resolves.
//   3. (Future) recent / common picks — out of scope for v1.
//
// Calorie preview reads through the standard resolver chain so the
// number on this sheet matches the dashboard's number after submit.

export default function EatQuickSheet({
  userId,
  pantry = [],
  shoppingList,
  setShoppingList,
  onClose,
}) {
  const ingredientInfo = useIngredientInfo();
  const brandNutrition = useBrandNutrition();
  const { push: pushToast } = useToast();
  const { logQuickConsumption, loading } = useConsumptionLogs({
    userId,
    brandNutrition,
    getInfo: ingredientInfo?.getInfo,
  });

  const [query, setQuery] = useState("");
  const [canonicalId, setCanonicalId] = useState(null);
  const [brand, setBrand] = useState("");
  const [amount, setAmount] = useState("");
  const [unit, setUnit] = useState("");
  const [finishedPackage, setFinishedPackage] = useState(false);
  const [addToShopping, setAddToShopping] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanLookupLoading, setScanLookupLoading] = useState(false);

  const inputRef = useRef(null);
  useEffect(() => {
    if (!canonicalId && !scanning) {
      const t = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [canonicalId, scanning]);

  // Search filter — sanctioned typeahead over BOTH canonical tiers
  // (bundled + dbCanonicals). The old hand-rolled loop over the
  // bundled INGREDIENTS array made every synthetic canonical (spam,
  // gjetost, …) invisible to this sheet while the kitchen could see
  // them fine — the classic forbidden pattern from CLAUDE.md.
  const matches = useMemo(() => searchCanonicals(query, 6), [query]);

  const canonical = canonicalId ? findIngredient(canonicalId) : null;

  // ── Package sizes — "I ate half the can" without knowing what a
  // can is. Chain: the package in YOUR kitchen (pantry row `max`,
  // the as-sold size) → observed popular sizes → AI typicalSizes
  // from ingredient_info. usePopularPackages already resolves the
  // last two tiers; we prepend the pantry tier and dedupe.
  const { rows: popularPkgs } = usePopularPackages(
    brand.trim() || null,
    canonicalId,
    3,
  );
  const packageSizes = useMemo(() => {
    if (!canonicalId) return [];
    const out = [];
    const seen = new Set();
    const push = (entry) => {
      if (!entry || !Number.isFinite(entry.amount) || entry.amount <= 0 || !entry.unit) return;
      const sig = `${entry.amount}|${entry.unit}`;
      if (seen.has(sig)) return;
      seen.add(sig);
      out.push(entry);
    };
    const mine = (pantry || []).find(
      (p) => (p?.canonicalId === canonicalId || p?.ingredientId === canonicalId)
        && Number(p?.max) > 0 && p?.unit,
    );
    if (mine) push({ amount: Number(mine.max), unit: mine.unit, source: "pantry" });
    for (const r of popularPkgs) push(r);
    return out.slice(0, 3);
  }, [canonicalId, pantry, popularPkgs]);
  const [pkgIdx, setPkgIdx] = useState(0);
  const selectedPkg = packageSizes[Math.min(pkgIdx, packageSizes.length - 1)] || null;

  const PACKAGE_FRACTIONS = [
    { label: "¼", value: 0.25 },
    { label: "⅓", value: 1 / 3 },
    { label: "½", value: 0.5 },
    { label: "¾", value: 0.75 },
    { label: "ALL", value: 1 },
  ];
  function applyPackageFraction(frac) {
    if (!selectedPkg) return;
    const amt = Math.round(selectedPkg.amount * frac * 100) / 100;
    setAmount(String(amt));
    setUnit(selectedPkg.unit);
    // Ate the whole thing → pre-flip the finished-package toggle so
    // the re-add-to-shopping offer is one tap away.
    if (frac === 1) setFinishedPackage(true);
  }

  // Pick a sensible default unit + amount the moment a canonical lands.
  // For count-style canonicals (each, stick, slice), default amount=1.
  // For weight/volume, leave amount empty so the user types it.
  function bindCanonical(id, autoBrand) {
    setCanonicalId(id);
    setPkgIdx(0);
    const c = findIngredient(id);
    if (!c) return;
    const preferred = c.preferredUnit?.us || c.defaultUnit || c.unit || c.units?.[0]?.id || "g";
    setUnit(preferred);
    // Only overwrite brand when autoBrand was explicitly passed (scan
    // path supplies it). Manual canonical picks leave the existing
    // brand alone — preserves the case where SCAN found the brand
    // but couldn't bind the canonical, then user manually picked.
    if (typeof autoBrand === "string" && autoBrand) setBrand(autoBrand);
    setQuery("");
    const isCountish = ["each", "egg", "slice", "stick", "piece"].includes(preferred);
    if (isCountish && !amount) setAmount("1");
  }

  // Calorie preview — computed every render against current amount/unit.
  const kcalPreview = useMemo(() => {
    if (!canonical) return null;
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) return null;
    if (!unit) return null;
    const synthetic = {
      id: null,
      ingredientId: canonical.id,
      canonicalId: canonical.id,
      brand: brand || null,
      amount: null, unit: null, nutritionOverride: null,
      packageAmount: null, packageUnit: null, countWeightG: null,
    };
    const { nutrition } = resolveNutrition(synthetic, { brandNutrition, getInfo: ingredientInfo?.getInfo });
    if (!nutrition) return null;
    const countWeightG = effectiveCountWeightG(synthetic, canonical);
    const factor = scaleFactor({ amount: amt, unit }, canonical, nutrition, { countWeightG });
    if (factor == null || !Number.isFinite(factor)) return null;
    if (typeof nutrition.kcal !== "number") return null;
    return Math.round(nutrition.kcal * factor);
  }, [canonical, amount, unit, brand, brandNutrition, ingredientInfo]);

  async function handleBarcode(barcode) {
    setScanning(false);
    if (!barcode) return;
    setScanLookupLoading(true);
    try {
      const result = await lookupBarcode(barcode, {
        brandNutritionRows: Array.isArray(brandNutrition?.rows) ? brandNutrition.rows : [],
      });
      if (!result?.found) {
        pushToast("Couldn't find that product — try typing it instead.", { kind: "warning" });
        return;
      }
      const name = result.productName || result.brand || "";
      const inferredId = name ? inferCanonicalFromName(name) : null;
      if (inferredId) {
        bindCanonical(inferredId, result.brand || "");
        pushToast(`Found: ${findIngredient(inferredId)?.name}`, { kind: "info" });
      } else {
        // No canonical bound — let user type a refining term.
        pushToast(`Found "${name}" — pick a category below.`, { kind: "info" });
        setQuery(name.slice(0, 40));
        if (result.brand) setBrand(result.brand);
      }
    } catch (e) {
      console.warn("[EatQuickSheet] barcode lookup failed:", e?.message || e);
      pushToast("Scan failed — try typing it.", { kind: "warning" });
    } finally {
      setScanLookupLoading(false);
    }
  }

  async function handleSubmit() {
    if (!canonicalId) {
      pushToast("Pick what you ate first.", { kind: "warning" });
      return;
    }
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      pushToast("Enter an amount.", { kind: "warning" });
      return;
    }
    const res = await logQuickConsumption({
      canonicalId,
      brand: brand.trim() || null,
      amount: amt,
      unit,
      mealSlot: inferMealSlot(new Date()),
    });
    if (!res.ok) {
      pushToast(`Log failed: ${res.error}`, { kind: "warning" });
      return;
    }
    const kcalText = res.kcal != null ? ` (${Math.round(res.kcal)} kcal)` : "";
    pushToast(`Logged ${canonical?.name || "it"}${kcalText}`, { emoji: "🍽️", kind: "info" });

    if (finishedPackage && addToShopping && typeof setShoppingList === "function") {
      const newItem = {
        id: crypto.randomUUID(),
        ingredientId: canonicalId,
        name: canonical?.name || "",
        emoji: canonical?.emoji || "",
        amount: 1,
        unit: canonical?.preferredUnit?.us || canonical?.defaultUnit || canonical?.units?.[0]?.id || "each",
        // "pantry" not null — category retired for hub chains, so
        // most canonicals lack the field, and explicit null violates
        // shopping_list_items' NOT NULL category column.
        category: canonical?.category || "pantry",
        source: "manual",
        priceCents: null,
        ownerId: userId,
        purchasedAt: null,
        purchasedPantryItemId: null,
        purchasedTripId: null,
        ...(brand.trim() ? { brand: brand.trim() } : {}),
      };
      setShoppingList(prev => [...prev, newItem]);
      pushToast(`Added ${canonical?.name} to shopping list`, { emoji: "🛒", kind: "info" });
    }
    onClose?.();
  }

  // ── Layout ─────────────────────────────────────────────────────────

  if (scanning) {
    return (
      <ModalSheet onClose={() => setScanning(false)} zIndex={Z.modal + 5}>
        <div style={{ padding: 16 }}>
          <div style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 22, color: COLOR.ink, marginBottom: 12 }}>
            Scan a UPC
          </div>
          <BarcodeScanner
            onDetected={handleBarcode}
            onCancel={() => setScanning(false)}
            mode="single"
          />
        </div>
      </ModalSheet>
    );
  }

  return (
    <ModalSheet onClose={onClose} zIndex={Z.modal + 5}>
      <div style={{ padding: 20, paddingBottom: 32, maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
          <div style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 28, fontWeight: 500, color: COLOR.ink }}>
            Eat
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: "none", border: "none", fontFamily: FONT.mono, fontSize: 13, color: COLOR.sub, cursor: "pointer", padding: 4 }}
          >
            cancel
          </button>
        </div>
        <div style={{ fontFamily: FONT.sans, fontSize: 13, color: COLOR.dim, marginBottom: 16, lineHeight: 1.4 }}>
          Quick calorie log. No inventory tracking.
        </div>

        {/* Search + scan row */}
        {!canonical && (
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="What did you eat?"
              style={{
                flex: 1,
                padding: "12px 14px",
                background: COLOR.deep,
                border: `1px solid ${COLOR.edge}`,
                borderRadius: 12,
                fontFamily: FONT.sans,
                fontSize: 15,
                color: COLOR.ink,
                outline: "none",
              }}
            />
            <button
              type="button"
              onClick={() => setScanning(true)}
              disabled={scanLookupLoading}
              style={{
                padding: "0 16px",
                background: COLOR.deep,
                border: `1px solid ${COLOR.edge}`,
                borderRadius: 12,
                fontFamily: FONT.mono,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.5,
                color: COLOR.ink,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {scanLookupLoading ? "…" : "SCAN"}
            </button>
          </div>
        )}

        {/* Search results */}
        {!canonical && matches.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
            {matches.map((ing) => (
              <button
                key={ing.id}
                type="button"
                onClick={() => bindCanonical(ing.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  background: COLOR.deep,
                  border: `1px solid ${COLOR.edge}`,
                  borderRadius: 10,
                  fontFamily: FONT.sans,
                  fontSize: 14,
                  color: COLOR.ink,
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span style={{ fontSize: 18 }}>{ing.emoji || "•"}</span>
                <span style={{ flex: 1 }}>{ing.name}</span>
                {ing.nutrition?.kcal != null && (
                  <span style={{ fontFamily: FONT.mono, fontSize: 11, color: COLOR.sub }}>
                    {Math.round(ing.nutrition.kcal)} kcal / 100g
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Selected canonical */}
        {canonical && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 14px",
            background: COLOR.deep,
            border: `1px solid ${COLOR.gold}`,
            borderRadius: 12,
            marginBottom: 16,
          }}>
            <span style={{ fontSize: 28 }}>{canonical.emoji || "🍽️"}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 18, color: COLOR.ink }}>
                {canonical.name}
              </div>
              {brand && (
                <div style={{ fontFamily: FONT.mono, fontSize: 11, color: COLOR.sub, letterSpacing: 0.3 }}>
                  {brand.toUpperCase()}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setCanonicalId(null); setBrand(""); setAmount(""); setUnit(""); setPkgIdx(0); setFinishedPackage(false); setAddToShopping(false); }}
              style={{ background: "none", border: "none", fontFamily: FONT.mono, fontSize: 11, color: COLOR.sub, cursor: "pointer" }}
            >
              change
            </button>
          </div>
        )}

        {/* Amount + unit */}
        {canonical && (
          <>
            <div style={{ fontFamily: FONT.mono, fontSize: 11, color: COLOR.gold, letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 6 }}>
              How much?
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^0-9./]/g, ""))}
                placeholder="0"
                autoFocus
                style={{
                  width: 80,
                  padding: "10px 12px",
                  background: COLOR.deep,
                  border: `1px solid ${COLOR.edge}`,
                  borderRadius: 10,
                  fontFamily: FONT.mono,
                  fontSize: 16,
                  color: COLOR.ink,
                  outline: "none",
                  textAlign: "center",
                }}
              />
              <select
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  background: COLOR.deep,
                  border: `1px solid ${COLOR.edge}`,
                  borderRadius: 10,
                  fontFamily: FONT.sans,
                  fontSize: 14,
                  color: COLOR.ink,
                  outline: "none",
                  appearance: "menulist",
                }}
              >
                {(canonical.units || [{ id: canonical.unit || "g", label: canonical.unit || "g" }]).map((u) => (
                  <option key={u.id} value={u.id}>{u.label || u.id}</option>
                ))}
                {/* A package-fraction pick can set a unit outside the
                    canonical's ladder (e.g. a pantry row stored in
                    "count") — surface it so the select stays in sync
                    with state instead of silently showing the first
                    ladder option. */}
                {unit && !(canonical.units || []).some((u) => u.id === unit) && (
                  <option value={unit}>{unit}</option>
                )}
              </select>
            </div>
            <div style={{ fontFamily: FONT.mono, fontSize: 12, color: kcalPreview != null ? COLOR.gold : COLOR.sub, marginBottom: 16, letterSpacing: 0.4 }}>
              {kcalPreview != null ? `≈ ${kcalPreview} kcal` : "calories will calculate when you enter an amount"}
            </div>

            {/* Package fractions — log "half the can" without knowing
                what the can is. Sizes resolve from the user's own
                pantry row, then observed sizes, then AI typicalSizes. */}
            {packageSizes.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontFamily: FONT.forma, fontSize: 12, fontWeight: 600, color: COLOR.sub, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>
                  Or log by package
                </div>
                {packageSizes.length > 1 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                    {packageSizes.map((p, i) => (
                      <button
                        key={`${p.amount}|${p.unit}`}
                        type="button"
                        onClick={() => setPkgIdx(i)}
                        style={{
                          fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
                          color: i === pkgIdx ? COLOR.ink : COLOR.muted,
                          background: i === pkgIdx ? COLOR.goldDeep : COLOR.deep,
                          border: `1px solid ${i === pkgIdx ? COLOR.goldDim : COLOR.edge}`,
                          borderRadius: 999, padding: "6px 12px", cursor: "pointer",
                        }}
                      >
                        {p.amount} {p.countLabel || p.unit}{p.source === "pantry" ? " — yours" : ""}
                      </button>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: 6 }}>
                  {PACKAGE_FRACTIONS.map((f) => (
                    <button
                      key={f.label}
                      type="button"
                      onClick={() => applyPackageFraction(f.value)}
                      style={{
                        flex: 1,
                        fontFamily: FONT.forma, fontSize: 14, fontWeight: 600,
                        color: COLOR.ink, background: COLOR.deep,
                        border: `1px solid ${COLOR.edge}`,
                        borderRadius: 10, padding: "10px 0", cursor: "pointer",
                      }}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                {selectedPkg && (
                  <div style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.sub, marginTop: 6 }}>
                    of a {selectedPkg.amount} {selectedPkg.countLabel || selectedPkg.unit} package
                    {selectedPkg.source === "pantry" ? " (the one in your kitchen)" : ""}
                  </div>
                )}
              </div>
            )}

            {/* Optional brand text — quick + skippable */}
            <details style={{ marginBottom: 16 }}>
              <summary style={{ fontFamily: FONT.mono, fontSize: 11, color: COLOR.sub, cursor: "pointer", letterSpacing: 0.5, textTransform: "uppercase" }}>
                Brand (optional)
              </summary>
              <input
                type="text"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder="e.g. Vital Farms"
                style={{
                  width: "100%",
                  marginTop: 8,
                  padding: "10px 12px",
                  background: COLOR.deep,
                  border: `1px solid ${COLOR.edge}`,
                  borderRadius: 10,
                  fontFamily: FONT.sans,
                  fontSize: 14,
                  color: COLOR.ink,
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </details>

            {/* Finished package + shopping list */}
            <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, cursor: "pointer", padding: "8px 0" }}>
              <input
                type="checkbox"
                checked={finishedPackage}
                onChange={(e) => {
                  setFinishedPackage(e.target.checked);
                  if (!e.target.checked) setAddToShopping(false);
                }}
                style={{ width: 18, height: 18, accentColor: COLOR.leaf || COLOR.gold }}
              />
              <span style={{ fontFamily: FONT.sans, fontSize: 14, color: COLOR.ink }}>
                That finished the package
              </span>
            </label>
            {finishedPackage && (
              <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, cursor: "pointer", padding: "8px 0 8px 28px" }}>
                <input
                  type="checkbox"
                  checked={addToShopping}
                  onChange={(e) => setAddToShopping(e.target.checked)}
                  style={{ width: 18, height: 18, accentColor: COLOR.leaf || COLOR.gold }}
                />
                <span style={{ fontFamily: FONT.sans, fontSize: 14, color: COLOR.ink }}>
                  Add to shopping list
                </span>
              </label>
            )}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || !amount}
              className="mcm-focusable"
              style={{
                width: "100%",
                padding: "16px",
                background: loading || !amount ? COLOR.edge : (COLOR.leaf || COLOR.gold),
                border: "none",
                borderRadius: 14,
                fontFamily: FONT.mono,
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: 1.2,
                color: "#fff",
                cursor: loading || !amount ? "default" : "pointer",
                textTransform: "uppercase",
                touchAction: "manipulation",
              }}
            >
              {loading ? "Logging…" : "Log it"}
            </button>
          </>
        )}
      </div>
    </ModalSheet>
  );
}
